import { trackLinkedInConversion } from "@/components/LinkedInTracking";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Unified conversion tracking helper.
 * Fans out to LinkedIn Insight Tag + GA4 with safe window guards.
 */
export function trackConversion(
  eventName: string,
  metadata?: Record<string, string | number | boolean>,
) {
  if (typeof window === "undefined") return;

  // LinkedIn Insight Tag
  trackLinkedInConversion(eventName);

  // Google Analytics 4
  if (window.gtag) {
    try {
      window.gtag("event", eventName, metadata ?? {});
    } catch (error) {
      console.error("GA4 tracking error:", error);
    }
  }
}

// Mirror the hostname→GA4_ID mapping from index.html.
// isLegacyProd: www.meshly.ai / meshly.ai → legacy property (continuous history).
// else: beta, preview channels, localhost → new clean property.
const LEGACY_GA4_HOSTS = new Set(["www.meshly.ai", "meshly.ai"]);

export function getGa4MeasurementId(): string {
  if (typeof window === "undefined") return "G-1NYBMY28TG";
  return LEGACY_GA4_HOSTS.has(window.location.hostname)
    ? "G-1FM6WE865E"
    : "G-1NYBMY28TG";
}

/**
 * Best-effort async read of the GA4 client_id for the current property.
 *
 * Resolves with the client_id string when gtag is ready, or undefined if:
 *   - gtag is not loaded (SSR, ad-blocked, slow connection)
 *   - the callback is not called within 800 ms
 *   - any internal error occurs
 *
 * Never rejects. Never blocks form submission — callers must treat undefined
 * as "send without it."
 */
export function getGa4ClientId(): Promise<string | undefined> {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.gtag) {
      resolve(undefined);
      return;
    }

    const timer = setTimeout(() => resolve(undefined), 800);

    try {
      window.gtag(
        "get",
        getGa4MeasurementId(),
        "client_id",
        (clientId: unknown) => {
          clearTimeout(timer);
          resolve(typeof clientId === "string" && clientId ? clientId : undefined);
        },
      );
    } catch {
      clearTimeout(timer);
      resolve(undefined);
    }
  });
}
